import { useEffect, useState, useCallback, useMemo } from 'react';
import { api } from '@shared/lib/api';
import { timeAgo } from '../shared/timeAgo';
import PayoutStatusBadge from './PayoutStatusBadge';
import DateRangeSelector, { getFilterDates } from '@dashboard/shared/DateRangeSelector';
import type { DateFilter } from '@dashboard/shared/DateRangeSelector';

interface StripePayout {
  id: string;
  amount: number;
  status: string;
  type: string;
  created: number;
  arrival_date: number;
}

interface PayoutsResponse {
  success: boolean;
  payouts: StripePayout[];
  has_more: boolean;
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const [hovered, setHovered] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={handleCopy}
      className="transition-colors font-mono text-[10px]"
      title="Copy full ID"
      style={{ color: hovered ? 'var(--dash-text-tertiary)' : 'var(--dash-text-faint)' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {copied ? '\u2713' : '\u2398'}
    </button>
  );
}

function formatDate(unix: number) {
  return new Date(unix * 1000).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

interface Props {
  apiKey: string;
}

export default function StripeBankPayouts({ apiKey }: Props) {
  const [payouts, setPayouts] = useState<StripePayout[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [dateFilter, setDateFilter] = useState<DateFilter>({ range: '30d' });
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);
  const [hoveredLoadMore, setHoveredLoadMore] = useState(false);

  const filteredPayouts = useMemo(() => {
    const { start, end } = getFilterDates(dateFilter);
    return payouts.filter((p) => {
      const ts = p.created * 1000;
      if (start && ts < new Date(start).getTime()) return false;
      if (end && ts > new Date(end).getTime()) return false;
      return true;
    });
  }, [payouts, dateFilter]);

  const fetchPayouts = useCallback(async (startingAfter?: string) => {
    const params: Record<string, string> = {};
    if (startingAfter) params.starting_after = startingAfter;
    const data = await api.stripePayouts(apiKey, params) as PayoutsResponse;
    return data;
  }, [apiKey]);

  useEffect(() => {
    if (!apiKey) return;
    fetchPayouts()
      .then((data) => {
        setPayouts(data.payouts || []);
        setHasMore(data.has_more || false);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [apiKey, fetchPayouts]);

  const loadMore = async () => {
    if (payouts.length === 0) return;
    setLoadingMore(true);
    try {
      const lastId = payouts[payouts.length - 1].id;
      const data = await fetchPayouts(lastId);
      setPayouts((prev) => [...prev, ...(data.payouts || [])]);
      setHasMore(data.has_more || false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2
            className="font-sans text-sm font-medium"
            style={{ color: 'var(--dash-text-secondary)' }}
          >
            Bank Payouts
          </h2>
          <p
            className="font-sans text-xs"
            style={{ color: 'var(--dash-text-dim)' }}
          >
            Money sent to your bank account
          </p>
        </div>
        <DateRangeSelector value={dateFilter} onChange={setDateFilter} />
      </div>

      {loading ? (
        <div
          className="rounded p-6"
          style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}
        >
          <div className="animate-pulse space-y-3">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-4 rounded w-full"
                style={{ background: 'var(--dash-hover-bg)' }}
              />
            ))}
          </div>
        </div>
      ) : error ? (
        <div
          className="rounded p-6"
          style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}
        >
          <p className="font-sans text-sm" style={{ color: 'var(--dash-text-muted)' }}>{error}</p>
        </div>
      ) : filteredPayouts.length === 0 ? (
        <div
          className="rounded p-8 text-center"
          style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}
        >
          <div className="text-2xl mb-3 opacity-20">$</div>
          <p
            className="font-sans text-sm mb-1"
            style={{ color: 'var(--dash-text-muted)' }}
          >
            No bank payouts yet
          </p>
          <p
            className="font-sans text-xs max-w-md mx-auto"
            style={{ color: 'var(--dash-text-faint)' }}
          >
            Once Stripe processes your first payout (typically 2 business days after your first charge), it will appear here.
          </p>
        </div>
      ) : (
        <>
          <div className="rounded overflow-x-auto" style={{ border: '1px solid var(--dash-border)' }}>
            <table className="w-full text-sm font-sans">
              <thead>
                <tr style={{ background: 'var(--dash-card)', borderBottom: '1px solid var(--dash-border)' }}>
                  <th
                    className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium"
                    style={{ color: 'var(--dash-text-dim)' }}
                  >
                    Payout ID
                  </th>
                  <th
                    className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium"
                    style={{ color: 'var(--dash-text-dim)' }}
                  >
                    Amount
                  </th>
                  <th
                    className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium"
                    style={{ color: 'var(--dash-text-dim)' }}
                  >
                    Status
                  </th>
                  <th
                    className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium"
                    style={{ color: 'var(--dash-text-dim)' }}
                  >
                    Method
                  </th>
                  <th
                    className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium"
                    style={{ color: 'var(--dash-text-dim)' }}
                  >
                    Initiated
                  </th>
                  <th
                    className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium"
                    style={{ color: 'var(--dash-text-dim)' }}
                  >
                    Arrival
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredPayouts.map((payout, i) => (
                  <tr
                    key={payout.id}
                    className="transition-colors last:border-b-0"
                    style={{
                      background: hoveredRow === payout.id
                        ? 'var(--dash-hover-bg)'
                        : i % 2 === 0
                          ? 'var(--dash-row-even)'
                          : 'var(--dash-row-odd)',
                      borderBottom: '1px solid var(--dash-border-light)',
                    }}
                    onMouseEnter={() => setHoveredRow(payout.id)}
                    onMouseLeave={() => setHoveredRow(null)}
                  >
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <span
                          className="font-mono text-xs"
                          style={{ color: 'var(--dash-text-tertiary)' }}
                        >
                          {payout.id.slice(0, 11)}...
                        </span>
                        <CopyButton text={payout.id} />
                      </div>
                    </td>
                    <td
                      className="px-4 py-2.5 font-mono text-xs"
                      style={{ color: 'var(--dash-text-primary)' }}
                    >
                      ${(payout.amount / 100).toFixed(2)}
                    </td>
                    <td className="px-4 py-2.5">
                      <PayoutStatusBadge status={payout.status} />
                    </td>
                    <td
                      className="px-4 py-2.5 font-mono text-xs"
                      style={{ color: 'var(--dash-text-muted)' }}
                    >
                      {payout.type === 'bank_account' ? 'Bank Account' : payout.type === 'card' ? 'Card' : payout.type}
                    </td>
                    <td
                      className="px-4 py-2.5 font-mono text-xs"
                      style={{ color: 'var(--dash-text-dim)' }}
                    >
                      {timeAgo(new Date(payout.created * 1000))}
                    </td>
                    <td
                      className="px-4 py-2.5 font-mono text-xs"
                      style={{ color: 'var(--dash-text-dim)' }}
                    >
                      {formatDate(payout.arrival_date)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {hasMore && (
            <button
              onClick={loadMore}
              disabled={loadingMore}
              className="w-full py-2 mt-2 font-mono text-xs rounded transition-colors disabled:opacity-40"
              style={{
                border: '1px solid var(--dash-border)',
                color: hoveredLoadMore ? 'var(--dash-text-secondary)' : 'var(--dash-text-muted)',
                background: hoveredLoadMore ? 'var(--dash-hover-bg)' : 'transparent',
              }}
              onMouseEnter={() => setHoveredLoadMore(true)}
              onMouseLeave={() => setHoveredLoadMore(false)}
            >
              {loadingMore ? 'Loading...' : 'Load more'}
            </button>
          )}
        </>
      )}
    </div>
  );
}
